import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useAlerts } from "common";
import {
  Box,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import {
  FilterAlt as FilterAltIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useActivityLogContext } from "app/services/activityLogContext";
import { getActivityLogFilterOptions } from "app/services/activityLogService";

const ActivityLogFilters = () => {
  const { t } = useTranslation();
  const {
    filters,
    setFilters,
    resetFilters,
    setInitialLoad,
    setPagination,
  } = useActivityLogContext();
  const { setAlert } = useAlerts();
  
  const [localFilters, setLocalFilters] = useState(filters);

  const [filterOptions, setFilterOptions] = useState({
    users: [],
    actions: [],
    statuses: [],
    protocols: [],
    departments: [],
    nctIds: [],
  });

  useEffect(() => {
    getActivityLogFilterOptions(setFilterOptions, setAlert);
  }, [setAlert]);

  const handleLocalFilterChange = (field, value) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setFilters(localFilters);
    setPagination((prev) => ({ ...prev, page: 0 }));
    setInitialLoad(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      startDate: null,
      endDate: null,
      users: [],
      actions: [],
      statuses: [],
      protocols: [],
      departments: [],
      nctIds: [],
    };
    setLocalFilters(clearedFilters);
    setFilters(clearedFilters);
    setPagination((prev) => ({ ...prev, page: 0 }));
    resetFilters();
  };

  const hasActiveFilters = useMemo(() => {
    return (
      localFilters.startDate ||
      localFilters.endDate ||
      localFilters.users.length > 0 ||
      localFilters.actions.length > 0 ||
      localFilters.statuses.length > 0 ||
      localFilters.protocols.length > 0 ||
      localFilters.departments.length > 0 ||
      localFilters.nctIds.length > 0
    );
  }, [localFilters]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FilterAltIcon color="primary" />
        <Typography variant="h6">{t("activityLog.filters.title")}</Typography>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Filters listed flat per PEPDEV-7304 order (no grouping) */}
      <Stack spacing={2.5} sx={{ mb: 3 }}>
        {/* User (PEPDEV-7304 #1) */}
        <Autocomplete
          multiple
          size="small"
          options={filterOptions.users}
          getOptionLabel={(option) => option.name || option.id || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={filterOptions.users.filter((option) =>
            localFilters.users.includes(option.id),
          )}
          onChange={(event, newValue) =>
            handleLocalFilterChange(
              "users",
              newValue.map((option) => option.id),
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("activityLog.filters.users")}
              placeholder={t("activityLog.filters.usersPlaceholder")}
            />
          )}
        />

        {/* Date Range (PEPDEV-7304 #2) */}
        <Stack spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            {t("activityLog.filters.dateRange")}
          </Typography>
          <DatePicker
            label={t("activityLog.filters.startDate")}
            value={localFilters.startDate}
            onChange={(newValue) => handleLocalFilterChange("startDate", newValue)}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
              },
            }}
          />
          <DatePicker
            label={t("activityLog.filters.endDate")}
            value={localFilters.endDate}
            onChange={(newValue) => handleLocalFilterChange("endDate", newValue)}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
              },
            }}
          />
        </Stack>

        {/* Protocol Number (PEPDEV-7304 #3) */}
        <Autocomplete
          multiple
          freeSolo
          autoSelect
          size="small"
          options={filterOptions.protocols}
          value={localFilters.protocols}
          onChange={(event, newValue) => handleLocalFilterChange("protocols", newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("activityLog.filters.protocols")}
              placeholder={t("activityLog.filters.protocolsPlaceholder")}
            />
          )}
        />

        {/* Status (PEPDEV-7304 #4 - moved up from bottom) */}
        <Autocomplete
          multiple
          size="small"
          options={filterOptions.statuses}
          value={localFilters.statuses}
          onChange={(event, newValue) => handleLocalFilterChange("statuses", newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("activityLog.filters.statuses")}
              placeholder={t("activityLog.filters.statusesPlaceholder")}
            />
          )}
        />

        {/* NCT # (PEPDEV-7304 #5) */}
        <Autocomplete
          multiple
          freeSolo
          autoSelect
          size="small"
          options={filterOptions.nctIds}
          value={localFilters.nctIds}
          onChange={(event, newValue) => handleLocalFilterChange("nctIds", newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("activityLog.filters.nctIds")}
              placeholder={t("activityLog.filters.nctIdsPlaceholder")}
            />
          )}
        />

        {/* Action (PEPDEV-7304 #7) */}
        <Autocomplete
          multiple
          size="small"
          options={filterOptions.actions}
          value={localFilters.actions}
          onChange={(event, newValue) => handleLocalFilterChange("actions", newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("activityLog.filters.actions")}
              placeholder={t("activityLog.filters.actionsPlaceholder")}
            />
          )}
        />

        {/* Study Department (PEPDEV-7304 #8) */}
        <Autocomplete
          multiple
          freeSolo
          autoSelect
          size="small"
          options={filterOptions.departments}
          value={localFilters.departments}
          onChange={(event, newValue) => handleLocalFilterChange("departments", newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("activityLog.filters.departments")}
              placeholder={t("activityLog.filters.departmentsPlaceholder")}
            />
          )}
        />
      </Stack>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleApplyFilters}
          startIcon={<FilterAltIcon />}
        >
          {t("activityLog.filters.apply")}
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleClearFilters}
          startIcon={<ClearIcon />}
          disabled={!hasActiveFilters}
        >
          {t("activityLog.filters.clear")}
        </Button>
      </Stack>

      {filterOptions.lastRefreshed && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: "block" }}
        >
          {t("activityLog.lastRefreshed")}:{" "}
          {format(new Date(filterOptions.lastRefreshed), "MM/dd/yyyy hh:mm:ss a")}
        </Typography>
      )}
    </Box>
  );
};

export default ActivityLogFilters;
